import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "./useAuth";
import { useToast } from "./useToast";
import { capture } from "../lib/analytics";
import { Property } from "../types";
import { DealStage } from "../lib/dealStages";

export interface WatchlistEntry {
  watchlistId: string;
  propertyId: string;
  notes: string;
  // Deal stage (#3075). null/absent = no stage set.
  stage?: DealStage | null;
  addedAt: string;
  property: Property | null;
}

// #782: shape of the 403 AddToWatchlist returns when the plan's watchlist
// cap (#780, `watchlistCap` in backend/internal/handlers/watchlist_handler.go)
// is exceeded — { error: "pro_required", upgrade_url, limit, plan, message }.
// Only the fields the UI needs are carried through.
export interface WatchlistCapInfo {
  limit: number;
  message: string;
  upgradeUrl: string;
}

export function useWatchlist(onCapExceeded?: (info: WatchlistCapInfo) => void) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [entries, setEntries] = useState<WatchlistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  // Depend on user.id, not the user object. Supabase's TOKEN_REFRESHED
  // event produces a new user object reference on tab-return, which
  // would otherwise re-trigger the fetch + loading flicker even though
  // the logical user is unchanged.
  const userId = user?.id;
  const refresh = useCallback(async () => {
    if (!userId) {
      setEntries([]);
      setWatchedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ data: WatchlistEntry[] }>("/watchlist");
      setEntries(data.data || []);
      setWatchedIds(new Set((data.data || []).map((e) => e.propertyId)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("watchlist fetch failed", err);
      toast("Could not load your watchlist. Please reload the page.", "error");
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Debounce refresh to avoid cascade of GET /watchlist calls
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debouncedRefresh = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => refresh(), 500);
  }, [refresh]);

  const toggle = useCallback(
    async (propertyId: string) => {
      if (!userId) return;
      const isWatched = watchedIds.has(propertyId);
      // optimistic
      const next = new Set(watchedIds);
      if (isWatched) next.delete(propertyId);
      else next.add(propertyId);
      setWatchedIds(next);
      setTogglingIds((prev) => new Set(prev).add(propertyId));
      try {
        if (isWatched) {
          await apiClient.delete(`/watchlist/${propertyId}`);
        } else {
          await apiClient.post("/watchlist", { property_id: propertyId });
          // #133 funnel event #2 — fires only on ADD (not remove), after
          // the POST succeeds. property_id only; county/status enrichment
          // is a follow-up (the hook doesn't carry the full Property).
          capture("watchlist_add", { property_id: propertyId });
        }
        debouncedRefresh();
      } catch (err: any) {
        // rollback
        setWatchedIds(watchedIds);
        // #782: the backend enforces a per-plan watchlist cap on ADD (#780)
        // and returns 403 { error: "pro_required", limit, message,
        // upgrade_url } when it's exceeded. Kept as defense-in-depth even
        // though the client-side pre-check (App.tsx's watchLimitMax, fed by
        // getPlanCapabilities in lib/planCapabilities.ts — #677 fixed a bug
        // where it shortcut on the coarse `isPro` flag and never caught a
        // Scout user at their real cap of 10) now mirrors the server for
        // every tier: this path still covers drift between the two, a stale
        // client bundle, or a stale count from a second tab. Surface the
        // real server message via onCapExceeded (wired to the existing
        // UpgradePrompt modal) instead of the generic failure toast below,
        // so the user sees an accurate "you're at your plan's limit"
        // explanation rather than being told this was a transient error.
        if (
          !isWatched &&
          err?.response?.status === 403 &&
          err?.response?.data?.error === "pro_required"
        ) {
          const data = err.response.data || {};
          const limit = typeof data.limit === "number" ? data.limit : 3;
          onCapExceeded?.({
            limit,
            message:
              data.message ||
              `Your current plan allows up to ${limit} watchlisted properties. Upgrade to Pro for unlimited.`,
            upgradeUrl: data.upgrade_url || "/pricing",
          });
        } else {
          toast(
            isWatched
              ? "Failed to remove from watchlist — please try again"
              : "Failed to add to watchlist — please try again",
            "error"
          );
        }
      } finally {
        setTogglingIds((prev) => {
          const n = new Set(prev);
          n.delete(propertyId);
          return n;
        });
      }
    },
    [userId, watchedIds, debouncedRefresh, toast, onCapExceeded]
  );

  const updateNotes = useCallback(
    async (propertyId: string, notes: string) => {
      if (!userId) return;
      try {
        await apiClient.patch(`/watchlist/${propertyId}`, { notes });
        refresh();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("notes update failed", err);
        toast("Failed to save notes — please try again", "error");
      }
    },
    [userId, refresh, toast]
  );

  const getNotes = useCallback(
    (propertyId: string) => entries.find((e) => e.propertyId === propertyId)?.notes || "",
    [entries]
  );

  // Deal stage (#3075): optimistic, reverted with a toast if the save fails.
  const setStage = useCallback(
    async (propertyId: string, stage: DealStage | null) => {
      if (!userId) return;
      const prev = entries.find((e) => e.propertyId === propertyId)?.stage ?? null;
      const apply = (s: DealStage | null) =>
        setEntries((es) => es.map((e) => (e.propertyId === propertyId ? { ...e, stage: s } : e)));
      apply(stage);
      try {
        await apiClient.patch(`/watchlist/${propertyId}/stage`, { stage });
      } catch {
        apply(prev);
        toast("Failed to save the deal stage — please try again", "error");
      }
    },
    [userId, entries, toast]
  );

  const getStage = useCallback(
    (propertyId: string): DealStage | null => entries.find((e) => e.propertyId === propertyId)?.stage ?? null,
    [entries]
  );

  // Bulk watch (#3079): POSTs the ids in order and stops at the plan's cap
  // (the server's 403), handing that to onCapExceeded like a single add.
  // Returns how many were added.
  const addMany = useCallback(
    async (ids: string[]): Promise<number> => {
      if (!userId) return 0;
      let added = 0;
      for (const id of ids) {
        if (watchedIds.has(id)) continue;
        try {
          await apiClient.post("/watchlist", { property_id: id });
          added++;
        } catch (err: any) {
          if (err?.response?.status === 403 && err?.response?.data?.error === "pro_required") {
            const data = err.response.data || {};
            const limit = typeof data.limit === "number" ? data.limit : 3;
            onCapExceeded?.({
              limit,
              message:
                data.message ||
                `Your current plan allows up to ${limit} watchlisted properties. Upgrade to Pro for unlimited.`,
              upgradeUrl: data.upgrade_url || "/pricing",
            });
          } else {
            toast("Some properties couldn't be added to your watchlist.", "error");
          }
          break;
        }
      }
      await refresh();
      return added;
    },
    [userId, watchedIds, onCapExceeded, toast, refresh]
  );

  return {
    entries,
    watchedIds,
    loading,
    toggle,
    addMany,
    updateNotes,
    getNotes,
    setStage,
    getStage,
    refresh,
    togglingIds,
  };
}
